import { ReactNode } from 'react';
import { useSelector } from 'react-redux';

import {
    FieldViewportProvider,
    PlaybackProvider,
    PlayEditorProvider,
    PlayThemeProvider,
    SelectedProvider,
} from '@labradorsports/play-rendering';
import { useSite } from '@labradorsports/utils';

import { useDispatcher } from '../shared/hooks/index.js';
import { useCustomTheme } from '../shared/index.js';
import {
    RootState,
    playEditorActions,
    Selectors,
    useTeamPlaybookQuery,
    useLoadEntityLogoQuery,
    RouterSelectors,
    routerActions,
    useMyPlaybookQuery,
} from '../store/index.js';

import { PluginsProvider } from './providers/index.js';

export interface PlayEditorWrapperProps {
    children: ReactNode;
    dimensions?: Dimensions;
    readOnly?: boolean;
}

const PlayEditorWrapper: FC<PlayEditorWrapperProps> = ({ children, dimensions, readOnly }) => {
    const { Config } = useSite();
    const selectFrame = useDispatcher(playEditorActions.SelectFrame);
    const openPlay = useDispatcher(routerActions.OpenPlay);

    const teamLogoId = useSelector((state: RootState) => {
        if (state.playEditor.play?.branding) return state.playEditor.play.branding.logoEntityId;

        return Selectors.currentTeam(state)?.programId;
    });

    const {
        data: { shared = [] },
    } = useTeamPlaybookQuery();

    const playId = useSelector(Selectors.playId);

    const isTeamPlay = shared.some((play) => play.id === playId);

    const communityPlayId = useSelector(RouterSelectors.communityPlayId);
    const isCommunityPlay = useSelector((state: RootState) => communityPlayId?.includes(state.playEditor.play?.id));

    const playData = useSelector((state: RootState) => state.playEditor.play);
    const debugOptions = useSelector((state: RootState) => state.playEditor.debugOptions);
    const debugMarkers = useSelector((state: RootState) => state.playEditor.debugMarkers);
    const currentFrameIdx = useSelector((state: RootState) => state.playEditor.currentFrame);
    const { muiTheme } = useCustomTheme();
    const branding = {
        colorPrimary: muiTheme.palette.brandPrimary.main,
        colorSecondary: muiTheme.palette.brandSecondary.main,
    };
    const { data: teamLogoUrl } = useLoadEntityLogoQuery({ programId: teamLogoId });

    if (!playData) {
        if (playId?.startsWith('__')) {
            openPlay();
        }

        return null;
    }

    return (
        <PlayEditorProvider
            dimensions={dimensions}
            playData={playData}
            debugOptions={debugOptions}
            debugMarkers={debugMarkers}
            currentFrameIdx={currentFrameIdx}
            selectFrame={selectFrame}
            fieldUrl={Config.SiteConfig.FriendlyUrl}
            readOnly={readOnly}
        >
            <PlayThemeProvider branding={branding} teamLogoUrl={isTeamPlay || isCommunityPlay ? teamLogoUrl : null}>
                <FieldViewportProvider>
                    <PlaybackProvider>
                        <SelectedProvider>
                            <PluginsProvider>{children}</PluginsProvider>
                        </SelectedProvider>
                    </PlaybackProvider>
                </FieldViewportProvider>
            </PlayThemeProvider>
        </PlayEditorProvider>
    );
};

export default PlayEditorWrapper;
